/**
 * Ambiguity-aware catalog resolution for the manufacture import.
 *
 * The sales import's `resolveProduct` is first-writer-wins: when two products share a barcode
 * it silently picks one. For a manufacture that moves stock of the product AND its ingredients
 * that is not acceptable ("every reference must resolve to exactly one live product;
 * ambiguity is an error"), so this module records, next to the ordinary index, every key that
 * more than one DISTINCT product claims. `salesImport/resolve.ts` behaviour is untouched.
 *
 * "Live": the products dict only ever holds live products — Firestore deletes the document and
 * the backend list reads filter `deleted_at is null` — so no extra filtering is done here.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- product docs are untyped across Firestore/Supabase read modes (same ruling as utils/salesImport/resolve.ts) */

import {
  type ProductIndex,
  buildProductIndex,
  normalizeKey,
  numericVariant,
} from '../salesImport/resolve'

type Tier = 'byBarcode' | 'bySku' | 'byId' | 'byName'

/** Resolution order of `resolveProduct`: barcode → sku → id → name. */
const TIERS: Tier[] = ['byBarcode', 'bySku', 'byId', 'byName']

export interface ManufactureProductIndex extends ProductIndex {
  /** Per tier, the keys claimed by more than one distinct product. */
  collisions: Record<Tier, Set<string>>
  /**
   * Every id spelling a product document carries (`id`, `firestoreId`, `firestore_id`,
   * `supabaseId`, `supabase_id`) → that document. Used only to tell whether two records name the
   * same product across read modes (a Supabase-mode manufacture carries the product's uuid, a
   * Firestore one its Firestore id) — never for resolving a file cell, so a uuid typed into a file
   * still does not match.
   */
  byAnyId: Record<string, any>
}

const ID_FIELDS = ['id', 'firestoreId', 'firestore_id', 'supabaseId', 'supabase_id'] as const

/** No tier name contains it, so a `<tier>\0<key>` claim key belongs to exactly one tier. */
const CLAIM_SEP = '\u0000'

export type ProductResolution =
  | { status: 'found'; product: any }
  | { status: 'ambiguous' }
  | { status: 'not_found' }

/** The same document reached via `id` and `firestoreId` is one product, not a collision. */
const sameProduct = (a: any, b: any): boolean => {
  if (a === b) return true
  const ids = (product: any): string[] =>
    [normalizeKey(product?.id), normalizeKey(product?.firestoreId)].filter(
      (id): id is string => id !== null
    )
  const idsA = ids(a)
  return ids(b).some((id) => idsA.includes(id))
}

export const buildManufactureProductIndex = (
  productsDict: Record<string, any>
): ManufactureProductIndex => {
  // Kept as flat, never-escaping collections keyed `<tier>\0<key>` (not a record of per-tier
  // Maps/Sets): the W-writer census (scripts/wWriterCensus.ts) can then prove locally that
  // `claims.set` / `colliding.add` are not Firestore writes. The per-tier sets are built at the end.
  const claims = new Map<string, any[]>()
  const colliding = new Set<string>()

  const claim = (tier: Tier, rawKey: string | null, product: any): void => {
    if (!rawKey) return
    // Same keys buildProductIndex indexes: the literal key and its leading-zero-stripped variant.
    const numeric = numericVariant(rawKey)
    for (const key of numeric && numeric !== rawKey ? [rawKey, numeric] : [rawKey]) {
      const claimKey = `${tier}${CLAIM_SEP}${key}`
      const holders = claims.get(claimKey) ?? []
      if (!holders.some((holder) => sameProduct(holder, product))) holders.push(product)
      claims.set(claimKey, holders)
      if (holders.length > 1) colliding.add(claimKey)
    }
  }

  const byAnyId: Record<string, any> = {}
  for (const product of Object.values(productsDict || {})) {
    if (!product) continue
    for (const field of ID_FIELDS) {
      const id = normalizeKey(product[field])
      if (id !== null && byAnyId[id] === undefined) byAnyId[id] = product
    }
    claim('byBarcode', normalizeKey(product.barcode), product)
    claim('bySku', normalizeKey(product.sku), product)
    claim('byId', normalizeKey(product.id), product)
    claim('byId', normalizeKey(product.firestoreId), product)
    claim('byName', normalizeKey(product.name)?.toLowerCase() ?? null, product)
  }

  const collidingKeys: Record<Tier, string[]> = { byBarcode: [], bySku: [], byId: [], byName: [] }
  for (const claimKey of colliding) {
    for (const tier of TIERS) {
      const prefix = `${tier}${CLAIM_SEP}`
      if (claimKey.startsWith(prefix)) collidingKeys[tier].push(claimKey.slice(prefix.length))
    }
  }
  const collisions: Record<Tier, Set<string>> = {
    byBarcode: new Set(collidingKeys.byBarcode),
    bySku: new Set(collidingKeys.bySku),
    byId: new Set(collidingKeys.byId),
    byName: new Set(collidingKeys.byName),
  }

  return { ...buildProductIndex(productsDict), collisions, byAnyId }
}

/**
 * Same lookup order and key normalisation as `resolveProduct`: the first tier in which the key
 * matches decides. If that key is claimed by several products in that tier the answer is
 * `ambiguous` — it never falls through to a later tier.
 */
export const resolveProductStrict = (
  index: ManufactureProductIndex,
  article: string
): ProductResolution => {
  const key = normalizeKey(article)
  if (!key) return { status: 'not_found' }

  const numeric = numericVariant(key)
  const candidates = numeric && numeric !== key ? [key, numeric] : [key]

  for (const tier of TIERS) {
    const names = tier === 'byName'
    for (const candidate of names ? [key.toLowerCase()] : candidates) {
      const product = index[tier][candidate]
      if (product === undefined) continue
      if (index.collisions[tier].has(candidate)) return { status: 'ambiguous' }
      return { status: 'found', product }
    }
  }
  return { status: 'not_found' }
}
